<template>
  <div class="share-card top-card" :aria-label="$t('share.cardLabel')">
    <div class="card-label">{{ $t('share.cardLabel') }}</div>
    <div class="share-actions">
      <button
        v-for="target in targets"
        :key="target.id"
        type="button"
        class="share-btn"
        :class="`share-btn--${target.id}`"
        :title="target.title"
        :aria-label="target.title"
        @click="shareTo(target)"
      >
        <span class="share-icon" aria-hidden="true" v-html="target.icon"></span>
      </button>

      <button
        v-if="canNativeShare"
        type="button"
        class="share-btn share-btn--native"
        :title="$t('share.native')"
        :aria-label="$t('share.native')"
        @click="shareNative"
      >
        <span class="share-icon" aria-hidden="true" v-html="icons.native"></span>
      </button>

      <button
        type="button"
        class="share-btn share-btn--copy"
        :title="copied ? $t('share.linkCopied') : $t('share.copyLink')"
        :aria-label="copied ? $t('share.linkCopied') : $t('share.copyLink')"
        @click="copyLink"
      >
        <span class="share-icon" aria-hidden="true" v-html="copied ? icons.check : icons.link"></span>
      </button>
    </div>
  </div>
</template>

<script>
import { playtimeAPI } from '../../services/api'

// Inline so the card carries no external asset requests.
const icons = {
  x: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>',
  facebook: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>',
  reddit: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 01-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 01.042.52c0 2.694-3.13 4.87-6.993 4.87-3.865 0-6.994-2.176-6.994-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 014.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 01.14-.197.35.35 0 01.238-.042l2.906.617a1.214 1.214 0 011.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 00-.231.094.33.33 0 000 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 00.029-.463.33.33 0 00-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 00-.232-.095z"/></svg>',
  link: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>',
  check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
  native: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>'
}

// Rotated per click so someone sharing more than once does not post the same
// sentence twice. Kept free of the display name so the wording works whether
// you are sharing your own dashboard or someone else's — and every network
// drops this into an editable compose box anyway.
const SHARE_MESSAGES = [
  'check out my Steam playtime graph',
  'my Steam replay — no need to wait until December',
  'this is where all my Steam hours actually went',
  'my daily Steam playtime, tracked day by day',
  'I can see my Steam replay any time I want',
  'my last 30 days on Steam, hour by hour',
  'turns out I play more than I thought'
]

export default {
  name: 'ShareCard',
  props: {
    steamId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      icons,
      copied: false,
      copyTimer: null,
      lastMessageIndex: -1,
      warmedId: ''
    }
  },
  watch: {
    // Warm the card as soon as the dashboard shows a shareable profile, not
    // only at share-click. The click-time warm raced the social crawler: on
    // 2026-08-14 the card PNG and the tweet landed in the same minute and X
    // unfurled without the image. The worker's 6 h freshness check makes
    // repeat views a stat() no-op, so this costs one render per user per TTL.
    steamId: {
      immediate: true,
      handler() {
        this.warmCard()
      }
    }
  },
  computed: {
    shareUrl() {
      const id = (this.steamId || '').toString().trim()
      if (!/^\d{17}$/.test(id)) return ''
      const origin = typeof window !== 'undefined' && window.location
        ? window.location.origin
        : 'https://steamplayti.me'
      return `${origin}/user/${id}`
    },
    canNativeShare() {
      return typeof navigator !== 'undefined' && typeof navigator.share === 'function'
    },
    targets() {
      // No href here: the message is chosen at click time so it varies between
      // shares. Building it in a computed would freeze one sentence per render.
      return [
        { id: 'x', title: this.$t('share.onX'), icon: icons.x },
        { id: 'facebook', title: this.$t('share.onFacebook'), icon: icons.facebook },
        { id: 'reddit', title: this.$t('share.onReddit'), icon: icons.reddit }
      ]
    }
  },
  beforeUnmount() {
    if (this.copyTimer) clearTimeout(this.copyTimer)
  },
  methods: {
    // Fire-and-forget so a click-time call never blocks the compose window
    // (awaiting first would let the popup blocker kill it). Deduped per id so
    // the mount-time warm and the click-time warm don't both hit the api; a
    // failed warm stays retryable because warmedId is only set on success.
    warmCard() {
      const id = (this.steamId || '').toString().trim()
      if (!/^\d{17}$/.test(id) || this.warmedId === id) return
      playtimeAPI.prepareShareCard(id)
        .then(() => { this.warmedId = id })
        .catch(() => { /* unfurl falls back to the site image */ })
    },
    pickShareMessage() {
      const total = SHARE_MESSAGES.length
      let index = Math.floor(Math.random() * total)
      // Never repeat the message used on the previous click.
      if (total > 1 && index === this.lastMessageIndex) {
        index = (index + 1) % total
      }
      this.lastMessageIndex = index

      return SHARE_MESSAGES[index]
    },
    buildShareHref(id, message) {
      const url = encodeURIComponent(this.shareUrl)
      const text = encodeURIComponent(message)

      if (id === 'x') {
        return `https://twitter.com/intent/tweet?text=${text}&url=${url}`
      }

      if (id === 'facebook') {
        // Facebook's sharer takes only the URL — the card comes from og: tags,
        // so a message would be dropped anyway.
        return `https://www.facebook.com/sharer/sharer.php?u=${url}`
      }

      return `https://www.reddit.com/submit?url=${url}&title=${text}`
    },
    shareTo(target) {
      if (!this.shareUrl) return
      this.warmCard()
      const href = this.buildShareHref(target.id, this.pickShareMessage())
      window.open(href, '_blank', 'noopener,noreferrer,width=600,height=640')
    },
    async shareNative() {
      if (!this.shareUrl) return
      this.warmCard()
      const message = this.pickShareMessage()
      try {
        await navigator.share({ title: message, text: message, url: this.shareUrl })
      } catch (_) {
        // User dismissed the sheet — nothing to do.
      }
    },
    async copyLink() {
      if (!this.shareUrl) return
      this.warmCard()
      try {
        await navigator.clipboard.writeText(this.shareUrl)
      } catch (_) {
        const field = document.createElement('textarea')
        field.value = this.shareUrl
        field.setAttribute('readonly', '')
        field.style.position = 'absolute'
        field.style.left = '-9999px'
        document.body.appendChild(field)
        field.select()
        try { document.execCommand('copy') } catch (__) { /* clipboard unavailable */ }
        document.body.removeChild(field)
      }
      this.copied = true
      if (this.copyTimer) clearTimeout(this.copyTimer)
      this.copyTimer = setTimeout(() => { this.copied = false }, 1800)
    }
  }
}
</script>

<style scoped>
/* .top-card chrome (background/border/radius/gradient accent/hover) is inherited
   from the parent's scoped styles, which reach this component's ROOT element.
   They do NOT reach nested elements, so .card-label has to be restated here to
   match the uppercase label convention used by the other top cards. */
.share-card {
  justify-content: center;
}

.card-label {
  color: var(--muted);
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  margin-bottom: 6px;
}

.share-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.share-btn {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid var(--panel-border);
  border-radius: 8px;
  background: #0f141a;
  color: var(--muted);
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;
}

.share-btn:hover {
  color: var(--text);
  border-color: rgba(102, 192, 244, 0.55);
  transform: translateY(-1px);
}

.share-btn:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}

.share-btn--x:hover { color: #ffffff; }
.share-btn--facebook:hover { color: #1877f2; }
.share-btn--reddit:hover { color: #ff4500; }
.share-btn--copy:hover,
.share-btn--native:hover { color: var(--primary); }

.share-icon {
  display: inline-flex;
  width: 18px;
  height: 18px;
}

.share-icon :deep(svg) {
  width: 100%;
  height: 100%;
  display: block;
}
</style>
